import { InvoicePortalButtonCore, InvoicePortalButtonPlacement } from "@dillon/Components/InvoicePortalButtonCore";
import { shouldShowInvoicePortalButtonPlacement } from "@dillon/Utilities/InvoicePortalButtonVisibility";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { connect } from "react-redux";

const enum fields {
    variant = "variant",
    label = "label",
    link = "link",
    alignment = "alignment",
    customCSS = "customCSS",
    placement = "placement",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.variant]: "primary" | "secondary" | "tertiary";
        [fields.label]: string;
        [fields.link]: LinkFieldValue;
        [fields.alignment]: string;
        [fields.customCSS]: string;
        [fields.placement]: InvoicePortalButtonPlacement;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const session = state.context.session;
    const placement = (ownProps.fields?.placement ?? ownProps.generalFields?.placement ?? "header") as InvoicePortalButtonPlacement;

    return {
        isAuthenticated: !!(session?.isAuthenticated || session?.rememberMe) && !session?.isGuest,
        shouldShow: shouldShowInvoicePortalButtonPlacement(placement, state),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export const CmsInvoicePortalButton: FC<Props> = ({ fields, isAuthenticated, shouldShow }) => {
    if (!shouldShow) {
        return null;
    }

    return (
        <InvoicePortalButtonCore
            variant={fields.variant}
            label={fields.label}
            alignment={fields.alignment || "center"}
            customCSS={fields.customCSS}
            placement={fields.placement ?? "header"}
            isAuthenticated={isAuthenticated}
        />
    );
};

const defaultCustomCss = `.wrapper {
}

.InvoicePortalButton {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CmsInvoicePortalButton),
    definition: {
        group: "Basic",
        icon: "rectangle-wide",
        fieldDefinitions: [
            {
                name: fields.variant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.link,
                displayName: "Invoice Payment Portal Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: basicTab,
                isVisible: () => false,
            },
            {
                name: fields.alignment,
                displayName: "Button Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "center",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.placement,
                displayName: "Button Placement",
                tooltip: "Where the button will be displayed in the site's UI.",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Header (Desktop)", value: "header" },
                    { displayName: "Header (Mobile)", value: "headerMobile" },
                    { displayName: "My Account (Mobile)", value: "accountMobile" },
                    { displayName: "Invoice History Page (Mobile)", value: "invoiceHistory" },
                ],
                defaultValue: "header",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
